import React from 'react';
import {
  CheckCircleIcon, DownloadIcon, EyeIcon, RefreshIcon,
} from '@heroicons/react/outline';
import {
  formatDateCovered, formatDocumentDate, UPLOADS_PREPARING_PDF, UPLOADS_TRY_AGAIN,
} from '../../utils/referralUploads';

/**
 * ServiceUserUploadRow
 * --------------------
 * One document a school or a family attached (TDI-602), shared by the patient
 * overview card and the Documents page panel so the two can never describe
 * the same document differently (Build Spec: "one shared
 * ServiceUserUploadRow component").
 *
 *   variant="card"   the rating scale row style: the description, then the
 *                    type, the date covered and the provider underneath, and
 *                    View. No download on the overview (TDI-611).
 *   variant="panel"  the questionnaire panel row: numbered circle, status
 *                    badge, the five labels (description, type, date covered,
 *                    provider, uploaded date) and View plus PDF (TDI-610).
 *
 * A Word upload whose PDF copy is not ready yet says "Preparing PDF" and
 * offers no View or download: the Word original is never served (FR11).
 *
 * Props:
 *   - row: one row from rmsListReferralDocuments
 *   - index: its place in the list (panel numbering)
 *   - onView(row), onDownload(row): the actions; onDownload is omitted when
 *     the viewer may not download
 *   - onRetry(row): offered beside "Preparing PDF" once the copy has had long
 *     enough (TDI-762) — the fetch converts a failed copy again on request,
 *     so a stuck row can be freed instead of waiting for ever
 *   - busy: this row's request is in flight
 */
export default function ServiceUserUploadRow({
  row, index = 0, variant = 'panel', onView, onDownload, onRetry, busy = false,
}) {
  const covered = formatDateCovered(row.dateCovered);
  const uploaded = formatDocumentDate(row.uploadedAt);
  const ready = row.pdfReady !== false;
  const title = row.description || row.documentTypeLabel;
  const facts = [
    row.documentTypeLabel,
    covered ? `Covers ${covered}` : null,
    `Provided by ${String(row.providedByLabel || '').toLowerCase()}`,
  ].filter(Boolean);

  const viewButton = (
    <button
      type="button"
      onClick={() => onView(row)}
      disabled={busy}
      aria-label={`View ${title}`}
      className="inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-semibold text-gray-900 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      {busy ? (
        <RefreshIcon className="w-4 h-4 animate-spin" aria-hidden="true" />
      ) : (
        <EyeIcon className="w-4 h-4" aria-hidden="true" />
      )}
      View
    </button>
  );

  const preparing = (
    <span className="inline-flex items-center gap-2">
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
        <RefreshIcon className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} aria-hidden="true" />
        {UPLOADS_PREPARING_PDF}
      </span>
      {onRetry && (
        <button
          type="button"
          onClick={() => onRetry(row)}
          disabled={busy}
          aria-label={`${UPLOADS_TRY_AGAIN}: ${title}`}
          className="min-h-[44px] px-2 text-sm font-semibold text-primary underline disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          {UPLOADS_TRY_AGAIN}
        </button>
      )}
    </span>
  );

  if (variant === 'card') {
    return (
      <li className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white text-left">
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-medium text-gray-900 leading-tight break-words">{title}</h4>
          <p className="text-sm text-gray-600 mt-0.5">{facts.join(' · ')}</p>
        </div>
        <div className="flex-shrink-0">{ready ? viewButton : preparing}</div>
      </li>
    );
  }

  return (
    <li className="relative">
      <div
        className={`w-full flex items-center gap-6 my-1 py-3 px-4 rounded-lg ${
          ready ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
        }`}
      >
        <div
          className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2 flex items-center justify-center font-semibold text-lg ${
            ready ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-300 bg-gray-50 text-gray-400'
          }`}
          aria-hidden="true"
        >
          {index + 1}
        </div>

        <div className="flex-1 flex items-center justify-between gap-4 min-w-0">
          <div className="min-w-0 text-left">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-900 break-words">{title}</h3>
              {ready ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                  <CheckCircleIcon className="w-3.5 h-3.5" aria-hidden="true" />
                  Ready
                </span>
              ) : preparing}
            </div>
            <p className="text-sm text-gray-700 mt-1">{facts.join(' · ')}</p>
            {uploaded && <p className="text-sm text-gray-600 mt-0.5">{`Uploaded ${uploaded}`}</p>}
          </div>

          {ready && (
            <div className="flex items-center gap-3 flex-shrink-0">
              {viewButton}
              {onDownload && (
                <button
                  type="button"
                  onClick={() => onDownload(row)}
                  disabled={busy}
                  aria-label={`Download ${title} as PDF`}
                  className="inline-flex items-center justify-center min-h-[44px] min-w-24 px-4 py-2 rounded-md border border-gray-300 text-sm font-semibold text-gray-900 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  <DownloadIcon className="w-4 h-4 mr-1.5" aria-hidden="true" />
                  PDF
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
